import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

/**
 * Watchlist context — keeps a Set of property IDs the current user has
 * flagged as interested. Loaded once on mount; mutated optimistically on
 * heart-click via toggle().
 */

const WatchlistContext = createContext({ ids: new Set(), isInWatchlist: () => false, toggle: () => {} });

export function WatchlistProvider({ children, isLoggedIn }) {
  const [ids, setIds] = useState(new Set());
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) {
      setIds(new Set());
      setLoaded(true);
      return;
    }
    try {
      const r = await api.get('/api/watchlist/property-ids');
      setIds(new Set(r.data || []));
    } catch {
      // silent fail — watchlist is best-effort UX, not blocking
    } finally {
      setLoaded(true);
    }
  }, [isLoggedIn]);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = useCallback(async (propertyId) => {
    if (!isLoggedIn) {
      toast.error('Sign in to save properties');
      return;
    }
    // Optimistic UI: flip locally first, revert on failure
    const next = new Set(ids);
    const wasIn = next.has(propertyId);
    if (wasIn) next.delete(propertyId); else next.add(propertyId);
    setIds(next);
    try {
      const r = await api.post(`/api/watchlist/${propertyId}/toggle`);
      // Server is the truth — sync just in case
      const serverIn = !!r.data?.in_watchlist;
      if (serverIn !== !wasIn) {
        const correction = new Set(ids);
        if (serverIn) correction.add(propertyId); else correction.delete(propertyId);
        setIds(correction);
      }
      toast.success(serverIn ? 'Added to watchlist' : 'Removed from watchlist');
    } catch (err) {
      // Revert
      setIds(ids);
      toast.error('Could not update watchlist');
    }
  }, [ids, isLoggedIn]);

  const value = useMemo(() => ({
    ids, loaded,
    isInWatchlist: (id) => ids.has(id),
    toggle,
    refresh,
    count: ids.size,
  }), [ids, loaded, toggle, refresh]);

  return <WatchlistContext.Provider value={value}>{children}</WatchlistContext.Provider>;
}

export function useWatchlist() {
  return useContext(WatchlistContext);
}
